import React, { useEffect, createContext, useContext, useMemo, useState } from 'react';
import { useAPIData } from 'hooks/useAPIData';
import { useGlobalData } from 'dataProvider/globalData';
import Usage from 'constants/usage';

const ClientContext = createContext({});
// Separate from the data so a screen can tell "not loaded yet" from "loaded and empty".
// Turns false on failure too, so a screen gating on it degrades instead of hanging —
// but it stays true until the fetch runs at all, so only use it behind an authed route.
const ClientPendingContext = createContext(true);
// A guest session never fetches, so its usages are known-absent rather than pending.
// Without this, anything waiting for `usageLoaded` would wait forever on the guest screens.
const GUEST_CLIENT_DATA = { client_usages: [] };
export default function ClientDataProvider(props) {
    const { children } = props;

    const [
        {
            auth: { isAuthenticated },
        },
    ] = useGlobalData();

    // Only the usages are read from here (useClienHasUsage / useClientUsages) plus the
    // identity block, which needs no opt-in. Without `include` the response also carries
    // every mail template body — the bulk of it — on every page load.
    const [fetch, res] = useAPIData('/client-detail?include=usages', x => x, {});
    const ClientContextProvider = ClientContext.Provider;
    useEffect(() => {
        if (isAuthenticated) {
            fetch();
        }
    }, [isAuthenticated]);

    return (
        <ClientPendingContext.Provider value={res.pending}>
            <ClientContextProvider value={isAuthenticated ? res.data : GUEST_CLIENT_DATA}>
                {children}
            </ClientContextProvider>
        </ClientPendingContext.Provider>
    );
}

const useClientData = () => {
    return useContext(ClientContext);
};

const useClientDataPending = () => {
    return useContext(ClientPendingContext);
};

const useClienHasUsage = id => {
    const { client_usages } = useClientData();
    const [hasUsage, setHasUsage] = useState(false);

    useEffect(() => {
        const _hasUsage = Array.isArray(client_usages)
            ? client_usages.find(usage => usage.usage_id == id) != null
            : null;
        setHasUsage(_hasUsage);
    }, [client_usages]);

    return [hasUsage];
};

const useClientUsages = () => {
    const { client_usages } = useClientData();
    const usageLoaded = Array.isArray(client_usages);

    // Derived while rendering rather than in an effect: a component that reads a flag as it
    // mounts — form defaults, a fetch it decides on mount — would otherwise always get the
    // "everything enabled" placeholder on that first pass, even with the usages already here.
    const clientUsages = useMemo(
        () =>
            Object.keys(Usage).reduce((flags, key) => {
                flags[key] =
                    usageLoaded && !!client_usages.find(usage => usage.usage_id == Usage[key]);
                return flags;
            }, {}),
        [client_usages]
    );

    return { clientUsages, usageLoaded };
};

export { useClientData, useClientDataPending, useClienHasUsage, useClientUsages };
